// ============================================================
// Wegweisung und Gerüst (SPEC §7).
//
// Fünf Ebenen, jede mit genau einer Aufgabe:
//   Wurzel       Meldungen, der EINE Aufruf von GET /me, der Sprachabgleich
//   Startseite   „/" — abgemeldet die öffentliche Karte (E21), angemeldet die
//                Startseite (§32). DER EINZIGE Weg, der beides kann.
//   Kartenseite  `/karte` und `/projekte/:id` — die interne Karte, EIN Element
//                für beide Adressen (§32, E32.1)
//   Geschuetzt   alles Weitere: Sitzung nötig, dazu der Profilriegel
//   Shell        Kopfleiste + Reiterleiste um die Nicht-Karten-Bildschirme
//
// Die KARTE liegt ausserhalb der `Shell`, aber NICHT ohne Kopfleiste: sie
// bekommt `Kartenschale` — dieselben zwei Bauteile, nur ohne den `.schale`-
// Abstand. Das ist genau, worauf F2 gebaut hat: `.k-wrap` ist
// `position: fixed; inset: 0; z-index: 0` („die Karte liegt UNTER der
// schwebenden Kopfleiste"), und jede Tafel darin rechnet ihren Abstand aus
// `--kopf-frei` bzw. `--leiste-frei` — karte.css sagt an einer Stelle sogar
// ausdrücklich „über der Reiterleiste, die App.tsx stellt". In einer `.schale`
// mit `padding-top` säße die Karte darunter statt dahinter.
//
// Der Auffangkorb liegt ÜBER dem Outlet, aber UNTER der Kopfleiste: stürzt
// ein Bildschirm ab, soll man wegnavigieren können, statt neu zu laden. Sein
// `key` ist der Pfad — ohne ihn bliebe ein einmal abgestürzter Bildschirm auch
// dann als Fehlerseite stehen, wenn man längst woanders hingeklickt hat.
// AUSNAHME seit §32: die Kartenwege teilen sich den festen Schlüssel „karte"
// (siehe `Bildschirm`).
// ============================================================
import { Suspense, lazy, useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Spinner, Toaster } from "./components/ui";
import { Erfolg } from "./components/app/Erfolg";
import { MobilLeiste } from "./components/app/MobilLeiste";
import { ScreenBoundary } from "./components/app/ScreenBoundary";
import { Shell } from "./components/app/Shell";
import { TopNav } from "./components/app/TopNav";
import { Anmelden } from "./screens/Anmelden";
import { useIch } from "./api/hooks";
import { useEreignisse } from "./lib/ereignisse";
import { spracheVomServer, useT } from "./lib/texte";
import { istVerwaltung } from "./lib/rechte";
import { useAuthStore } from "./store/useAuthStore";

// Lazy nach den Pfaden aus SPEC §8 — jeder Bildschirm gehört genau einem
// Bauenden, und die Karte zieht MapLibre (über 800 kB) hinter sich her. Die
// ANMELDUNG bleibt eifrig geladen: sie ist für jeden Abgemeldeten der Weg
// hinein, und ein Nachladeschritt davor wäre ein leerer Bildschirm ohne Grund.
// Die Karte: derselbe Modulpfad wie `karteVorladen()` in lib/wege.ts — sonst
// füllte das Vorladen ein anderes Stück als das, aus dem hier gelesen wird.
const Karte = lazy(() => import("./screens/karte/Karte"));
// §32: die Startseite und die Seite der Abzeichen. Beide Vorgabe-Exporte;
// gebaut werden sie in ihren eigenen Ordnern, hier werden sie nur eingehängt.
const Start = lazy(() => import("./screens/start/Start"));
const Erfolge = lazy(() => import("./screens/erfolge/Erfolge"));
const Galerie = lazy(() => import("./screens/Galerie"));
const Board = lazy(() => import("./screens/Board"));
const Staende = lazy(() => import("./screens/Staende"));
// §52: die Vorschau — vierte Ansicht unter „Projekte“, eigener Ordner.
const Vorschau = lazy(() => import("./screens/vorschau/Vorschau"));
const Leitung = lazy(() => import("./screens/leitung/Leitung"));
const Runde = lazy(() => import("./screens/runde/Runde"));
const Abgaben = lazy(() => import("./screens/Abgaben"));
const Team = lazy(() => import("./screens/Team"));
const Buero = lazy(() => import("./screens/Buero"));
const Aktuelles = lazy(() => import("./screens/aktuelles/Aktuelles"));
// §51.8 f: „Gelernt" — die hausweite Sammlung aus den Rückblicken, eigener Ordner.
const Gelernt = lazy(() => import("./screens/gelernt/Gelernt"));
const Profil = lazy(() => import("./screens/Profil"));
const Wuensche = lazy(() => import("./screens/Wuensche"));
// §34: die Hauszeitung und die Links-Seite. Beide Vorgabe-Exporte aus ihren
// eigenen Ordnern; `/dienste` ist in `/links` aufgegangen (Umleitung unten).
const Zeitung = lazy(() => import("./screens/zeitung/Zeitung"));
const Links = lazy(() => import("./screens/links/Links"));
const Verwaltung = lazy(() => import("./screens/admin/Verwaltung"));

function Wartezeichen() {
  const { t } = useT();
  return (
    <div style={{ height: "60vh", display: "grid", placeItems: "center" }} role="status" aria-label={t.allgemein.laedt}>
      <Spinner size={22} />
    </div>
  );
}

function NichtGefunden() {
  const { t } = useT();
  const navigate = useNavigate();
  return (
    <div className="blatt blatt-schmal">
      <div className="card" style={{ padding: 28, textAlign: "center" }}>
        <h1 className="seitentitel">{t.fehler.nichtGefunden}</h1>
        <div className="reihe" style={{ justifyContent: "center", marginTop: 18 }}>
          <Button type="button" icon="house" onClick={() => navigate("/", { replace: true })}>
            {t.fehler.zurStartseite}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Wurzel */

function Wurzel() {
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  const abgemeldet = useAuthStore((s) => s.abgemeldet);

  // DER EINE Aufruf von GET /me. Er läuft über dieselbe Abfrage, aus der auch
  // jeder Bildschirm liest — ein eigener `fetch` hier wäre eine zweite
  // Anfrage, deren Antwort im Zwischenspeicher nicht ankäme.
  // `aktiv` ist wahr, solange der Status nicht „anonym" ist: beim Start
  // („unbekannt") wird gefragt, nach dem Abmelden nicht mehr.
  const { data: ich, isSuccess, isError } = useIch(status !== "anonym");

  useEffect(() => {
    if (isSuccess) angemeldet();
    // Ein 401 hat den Status über den Riegel in client.ts schon gesetzt. Jeder
    // ANDERE Fehler (Netz, 503 während eines Deploys) heißt hier ebenfalls
    // „nicht angemeldet": es gibt nichts zu zeigen, und die öffentliche Karte
    // ist ein ehrlicheres Bild als ein hängendes Wartezeichen.
    else if (isError) abgemeldet();
  }, [isSuccess, isError, angemeldet, abgemeldet]);

  // Der Ereignisstrom (SPEC §11.2). Er hängt am Anmeldestatus, läuft genau
  // EINMAL in der Anwendung und ersetzt das 60-s-Polling NICHT, sondern legt
  // sich davor: eine Änderung von nebenan steht in einer halben Sekunde im
  // Bild statt in einer Minute.
  useEreignisse();

  // Sobald das Profil da ist, gilt SEINE Sprache — so ist sie auf dem Telefon
  // dieselbe wie am Schreibtisch. `spracheVomServer` schreibt nur bei einem
  // Unterschied, sonst löste jeder Start eine Neuzeichnung aus.
  useEffect(() => {
    spracheVomServer(ich?.sprache);
  }, [ich?.sprache]);

  return (
    <>
      <Outlet />
      {/* GENAU EINMAL in der ganzen Anwendung. */}
      <Toaster />
      {/* Die Feier (§16.3) liegt aus demselben Grund hier: sie wird in
          `api/hooks.ts` ausgelöst und muss über JEDEM Bildschirm stehen —
          auch über der Karte und über einem offenen Projektmodal. */}
      <Erfolg />
    </>
  );
}

/* ------------------------------------------------ Startseite, Kartenseite */

/**
 * „/" — der einzige Weg, den es in beiden Zuständen gibt (E21): abgemeldet
 * das Schaufenster (die öffentliche Karte), angemeldet seit §32 die
 * Startseite. Die interne Karte liegt unter `/karte` (`Kartenseite`).
 *
 * „unbekannt" heißt: GET /me läuft noch. Dann ein Wartezeichen und KEINE
 * Entscheidung: würde hier sofort die öffentliche Karte gezeichnet, sähe ein
 * angemeldeter Mensch bei jedem Neuladen erst das Schaufenster und dann seine
 * Startseite — MapLibre geladen und gezeichnet für nichts.
 */
function Startseite() {
  const status = useAuthStore((s) => s.status);
  const { data: ich } = useIch(status === "angemeldet");

  if (status === "unbekannt") return <Wartezeichen />;
  if (status !== "angemeldet") return <Karte oeffentlich />;
  // Der Profilriegel auch hier: dieser Weg steht außerhalb von `Geschuetzt`.
  if (ich && ich.profil_vollstaendig === false) return <Navigate to="/profil" replace />;
  // Die `.schale` HIER und nicht in der Schale darüber: `Kartenschale` hat
  // keinen Abstand (die Karte liegt hinter der schwebenden Leiste), die
  // Startseite ist aber ein Blatt und muss unter ihr beginnen.
  return (
    <div className="schale">
      <Start />
    </div>
  );
}

/**
 * `/karte` UND `/projekte/:id` — EIN Element für beide Adressen.
 *
 * Die zwei Adressen sind Kinder mit `element: null` (siehe `router`); sie
 * steuern nur den Parameter bei, gezeichnet wird weiter diese Komponente. So
 * bleibt dieselbe Karte stehen, wenn das Projektmodal auf- und zugeht — als
 * eigener Weg wurde die Karte dabei neu aufgebaut und flog zurück auf die
 * Gesamtansicht, genau dann, wenn man sich die Grenze des eben geschlossenen
 * Projekts ansehen wollte. `useParams` liefert im Elternweg auch die
 * Parameter des Kindes. (Das wollte schon d35e4cc; erst der feste Schlüssel
 * in `Bildschirm` lässt es wirken.)
 */
function Kartenseite() {
  const status = useAuthStore((s) => s.status);
  const ort = useLocation();
  const { data: ich } = useIch(status === "angemeldet");
  const { id } = useParams();

  if (status === "unbekannt") return <Wartezeichen />;
  if (status !== "angemeldet") {
    // Ein Karten- oder Projektverweis ohne Anmeldung führt zur Anmeldung,
    // nicht ins Schaufenster — dort gäbe es das Projekt womöglich gar nicht.
    // MIT der Suche: `?reiter=team` aus einer Mail soll nach dem Code noch
    // da sein (Anmelden liest `von` über `anmeldeZiel`).
    return <Navigate to="/anmelden" replace state={{ von: ort.pathname + ort.search }} />;
  }
  // Ohne Name und Heimatbüro gibt es auf der Karte keinen Avatar und keinen
  // Platz dafür (E15) — deshalb zuerst das Profil, und zwar auch hier.
  if (ich && ich.profil_vollstaendig === false) return <Navigate to="/profil" replace />;
  return <Karte oeffentlich={false} projektId={id} />;
}

/**
 * `/aktuelles/:beitrag` → `/aktuelles?beitrag=…`. Der Wochenbrief und das
 * Protokoll der Runde verlinken einen Beitrag als eigenen Pfad; der Feed
 * öffnet ihn aber aus der Suche der Adresse. Bis §32 liefen diese Links auf
 * „Das gibt es nicht" — ein Umweg hier ist billiger als zwei Fassungen
 * derselben Adresse im Feed.
 */
function AktuellesVerweis() {
  const { beitrag } = useParams();
  return <Navigate to={`/aktuelles?beitrag=${encodeURIComponent(beitrag ?? "")}`} replace />;
}

/* ------------------------------------------------------------- Wächter */

function Geschuetzt() {
  const status = useAuthStore((s) => s.status);
  const ort = useLocation();
  const { data: ich } = useIch(status === "angemeldet");

  if (status === "unbekannt") return <Wartezeichen />;
  // Nach einem 401 aus irgendeinem Aufruf setzt der Riegel in client.ts den
  // Status auf „anonym", und dieser Zweig führt zur Anmeldung — OHNE Toast:
  // ein abgelaufenes Cookie ist kein Fehler.
  // `von` MIT der Suche — ein Verweis wie `/aktuelles?beitrag=…` soll nach der
  // Anmeldung derselbe sein.
  if (status !== "angemeldet") return <Navigate to="/anmelden" replace state={{ von: ort.pathname + ort.search }} />;
  // Der Profilriegel gilt überall AUSSER auf dem Profil selbst — sonst wäre
  // es eine Schleife.
  if (ich && ich.profil_vollstaendig === false && ort.pathname !== "/profil") return <Navigate to="/profil" replace />;
  return <Outlet />;
}

/** `/admin/*` verlangt zusätzlich die Rolle (SPEC §2). Kein Toast, keine Erklärung. */
function NurAdmin() {
  const status = useAuthStore((s) => s.status);
  const { data: ich } = useIch(status === "angemeldet");
  // `ich` ist einen Wimpernschlag lang undefined, obwohl die Sitzung steht —
  // in dieser Zeit ist die Antwort ein Wartezeichen und keine Umleitung.
  if (!ich) return <Wartezeichen />;
  if (!istVerwaltung(ich.rolle)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/* -------------------------------------------------------------- Wegtabelle */

/**
 * Die Schale der KARTE: dieselben zwei Bauteile wie in `Shell`, aber OHNE den
 * `.schale`-Abstand — die Karte füllt den Schirm und liegt hinter der
 * schwebenden Leiste, nicht unter ihr.
 *
 * Auch „/" hängt hier (§32): abgemeldet IST es die Karte. Angemeldet ist es
 * die Startseite, die sich ihre `.schale` selbst setzt — zwei Schalen für
 * denselben Weg, je nach Anmeldung, hätten den Weg zweimal gebraucht.
 *
 * Solange der Anmeldestatus „unbekannt" ist, bleibt die Leiste WEG. Sonst
 * zeichnete sie für den Bruchteil einer Sekunde ihre abgemeldete Fassung mit
 * dem Knopf „Anmelden", der gleich darauf gegen den eigenen Avatar getauscht
 * würde — genau das Flackern, das `Startseite` und `Kartenseite` oben vermeiden.
 */
function Kartenschale() {
  const status = useAuthStore((s) => s.status);
  const bereit = status !== "unbekannt";
  return (
    <>
      {bereit && <TopNav />}
      <Outlet />
      {bereit && status === "angemeldet" && <MobilLeiste />}
    </>
  );
}

/**
 * Auffangkorb + Nachladeanzeige um einen Bildschirm; `key` setzt beides je Weg zurück.
 *
 * `fest` ersetzt den Pfad als Schlüssel (§32, E32.1): die Kartenwege `/karte`
 * und `/projekte/:id` hängen an EINEM Schlüssel „karte". Mit dem Pfad als
 * Schlüssel wurde die Karte bei jedem Öffnen und Schließen eines Projekts
 * abgebaut und neu gezeichnet, gleich was `Kartenseite` darunter tat. Der
 * Preis, bewusst: eine abgestürzte Karte setzt sich beim Wechsel zwischen
 * diesen beiden Adressen nicht mehr von selbst zurück — erst beim Weg auf
 * eine andere Seite.
 */
function Bildschirm({ fest }: { fest?: string }) {
  const ort = useLocation();
  return (
    <ScreenBoundary key={fest ?? ort.pathname}>
      <Suspense fallback={<Wartezeichen />}>
        <Outlet />
      </Suspense>
    </ScreenBoundary>
  );
}

const router = createBrowserRouter([
  {
    element: <Wurzel />,
    children: [
      {
        path: "/anmelden",
        element: (
          <ScreenBoundary>
            <Anmelden />
          </ScreenBoundary>
        ),
      },
      {
        // Die Karte: Kopfleiste ja, `.schale` nein.
        element: <Kartenschale />,
        children: [
          {
            // „/": die Startseite, abgemeldet das Schaufenster. In der
            // Kartenschale, weil sie abgemeldet die Karte IST — die
            // `.schale` für die angemeldete Fassung setzt `Startseite` selbst.
            element: <Bildschirm />,
            children: [{ path: "/", element: <Startseite /> }],
          },
          {
            element: <Bildschirm fest="karte" />,
            children: [
              {
                element: <Kartenseite />,
                // Zwei Kinder, die selbst NICHTS zeichnen: sie steuern nur den
                // Parameter bei, gezeichnet wird weiter `Kartenseite`, und die
                // bleibt beim Wechsel `/karte` ↔ `/projekte/:id` montiert
                // (siehe dort und `Bildschirm`). `/projekte`, `/projekte/board`
                // und `/projekte/staende` gewinnen weiterhin — feste Abschnitte
                // schlagen `:id`. Die Adresse des Projekts bleibt
                // `/projekte/:id` und wird NICHT `/karte/projekte/:id`: sonst
                // müsste jeder Link in Mails und im Wochenbrief mitwandern.
                // `element: null` ausdrücklich: React Router warnt sonst bei
                // jedem Aufruf „Matched leaf route … does not have an element"
                // (die Prüfung fragt `!== undefined`, nicht nach Inhalt). `null`
                // rendert wie ein leeres Blatt nichts — `Kartenseite` zeichnet
                // kein `<Outlet />`, am Bild ändert sich also nichts.
                children: [
                  { path: "/karte", element: null },
                  { path: "/projekte/:id", element: null },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <Geschuetzt />,
        children: [
          {
            element: <Shell />,
            children: [
              {
                element: <Bildschirm />,
                children: [
                  // „Projekte" ist zuerst die Galerie; das Phasen-Board bleibt
                  // als zweiter Weg daneben (Umschalter im Seitenkopf).
                  { path: "/projekte", element: <Galerie /> },
                  { path: "/projekte/board", element: <Board /> },
                  // §23: die Standliste — dritte Ansicht neben Galerie und Board.
                  { path: "/projekte/staende", element: <Staende /> },
                  // §52: die Vorschau — wo sich Abgaben je Büro und Woche stauen. `"vorschau"`
                  // steht in `lib/wege.ts` FESTE, sonst hielte die Suche den Pfad für die Karte.
                  { path: "/projekte/vorschau", element: <Vorschau /> },
                  { path: "/abgaben", element: <Abgaben /> },
                  { path: "/team", element: <Team /> },
                  { path: "/aktuelles", element: <Aktuelles /> },
                  { path: "/aktuelles/:beitrag", element: <AktuellesVerweis /> },
                  // §51.8 f: „Gelernt" — ein eigener Weg und NICHT `/aktuelles/gelernt`: den
                  // finge `/aktuelles/:beitrag` als Beitragskennung. Kein Reiter in der Kopfleiste;
                  // die Zugänge stehen in Aktuelles und am Rückblick-Beitrag.
                  { path: "/gelernt", element: <Gelernt /> },
                  { path: "/bueros/:id", element: <Buero /> },
                  { path: "/profil", element: <Profil /> },
                  // §21: „Ich wünsche mir eine Funktion" — erreichbar über das
                  // Kontomenü, nicht über die Hauptleiste: man geht selten hin,
                  // und die Leiste ist mit neun Reitern mehr als voll (seit §32
                  // mit „Start" und „Karte", seit §34 mit „Zeitung" und „Links").
                  { path: "/wuensche", element: <Wuensche /> },
                  // §34: die Hauszeitung der Woche (`?woche=2026-W40`, am Telefon
                  // `?teil=branche`) und die Links — die Dienste des Hauses zum
                  // Anheften und „Meine Links". Beide unter der Schale wie
                  // `/aktuelles`: Sitzung nötig, abgemeldet zur Anmeldung.
                  { path: "/zeitung", element: <Zeitung /> },
                  { path: "/links", element: <Links /> },
                  // Die alte Dienste-Seite ist in `/links` aufgegangen. Umgeleitet
                  // und nicht gelöscht: Lesezeichen im Browser und das Kontomenü
                  // eines noch offenen alten Bündels zeigen weiter hierher.
                  { path: "/dienste", element: <Navigate to="/links" replace /> },
                  // §18.6: das Blatt der Geschäftsführung. Kein eigener Wächter —
                  // die Route antwortet allen anderen mit 403, und der Bildschirm
                  // sagt das in einem Satz, statt weiterzuleiten.
                  { path: "/leitung", element: <Leitung /> },
                  // §18.5: die Projektrunde. Ebenfalls ohne Wächter — wer in
                  // keinem Büro mit Rundentag sitzt, bekommt `null` und einen
                  // Satz dazu, und das ist kein Fehlerfall.
                  { path: "/runde", element: <Runde /> },
                  // §32: Serien und Abzeichen, nur für die Person selbst. Kein
                  // Wächter über die Stufe — auf „aus" und „etwas" erklärt die
                  // Seite sich und bietet den Schalter an, statt wegzuleiten.
                  { path: "/erfolge", element: <Erfolge /> },
                  {
                    element: <NurAdmin />,
                    children: [
                      { path: "/admin", element: <Navigate to="/admin/nutzer" replace /> },
                      // Ein Weg mit Bereichsparameter statt dreier Wege: die
                      // Verwaltung ist EIN Bildschirm mit drei Reitern, und
                      // drei Einträge hier wären drei Stellen, an denen ein
                      // vierter Bereich vergessen werden kann.
                      { path: "/admin/:bereich", element: <Verwaltung /> },
                    ],
                  },
                  { path: "*", element: <NichtGefunden /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
